import React, { useEffect, useState } from 'react';
import { DownloadIcon, ChevronRightIcon, HomeIcon } from 'lucide-react';
import { BatchList } from '../components/thetape/BatchList';
import { InvoiceList } from '../components/thetape/InvoiceList';
import { VerificationDetail } from '../components/thetape/VerificationDetail';
import { BatchHeader } from '../components/thetape/BatchHeader';
import { MOCK_BATCHES, Batch, Invoice } from '../lib/thetape-data';
import { Button } from '../components/ui/button';
type ViewState = 'batches' | 'invoices' | 'detail';
export function TheTape() {
  const [view, setView] = useState<ViewState>('batches');
  const [selectedBatch, setSelectedBatch] = useState<Batch | null>(null);
  const [selectedInvoice, setSelectedInvoice] = useState<Invoice | null>(null);
  useEffect(() => {
    document.title = 'Mint | The Tape';
  }, []);
  const handleBatchSelect = (batch: Batch) => {
    setSelectedBatch(batch);
    setView('invoices');
    window.scrollTo(0, 0);
  };
  const handleInvoiceSelect = (invoice: Invoice) => {
    setSelectedInvoice(invoice);
    setView('detail');
    window.scrollTo(0, 0);
  };
  const handleBackToBatches = () => {
    setSelectedBatch(null);
    setSelectedInvoice(null);
    setView('batches');
  };
  const handleBackToInvoices = () => {
    setSelectedInvoice(null);
    setView('invoices');
  };
  return <div className="min-h-screen bg-slate-950 text-slate-200 font-sans" data-id="element-2958">
      {/* Top Header */}
      <div className="border-b border-slate-800 bg-slate-900/50 backdrop-blur-sm sticky top-0 z-10" data-id="element-2959">
        <div className="px-6 py-4 flex justify-between items-center" data-id="element-2960">
          <div data-id="element-2961">
            <div className="flex items-center gap-2 text-xs font-mono text-slate-500 mb-1" data-id="element-2962">
              <span data-id="element-2963">CITY DESK</span>
              <ChevronRightIcon size={12} data-id="element-2964" />
              <span className="text-slate-300" data-id="element-2965">THE TAPE</span>
            </div>
            <h1 className="text-xl font-semibold text-white tracking-tight" data-id="element-2966">
              Batch Verification Report
            </h1>
          </div>

          <div className="flex items-center gap-4" data-id="element-2967">
            <div className="text-right hidden sm:block" data-id="element-2968">
              <div className="text-xs text-slate-500 font-mono" data-id="element-2969">DATA AS OF</div>
              <div className="text-sm text-slate-300 font-mono" data-id="element-2970">
                {new Date().toLocaleString()}
              </div>
            </div>
            <Button variant="outline" className="border-slate-700 bg-slate-800 hover:bg-slate-700 text-slate-300 gap-2" data-id="element-2971">
              <DownloadIcon size={16} data-id="element-2972" />
              Export
            </Button>
          </div>
        </div>

        {/* Breadcrumbs */}
        <div className="px-6 py-2 bg-slate-950 border-t border-slate-800 flex items-center gap-2 text-sm font-mono overflow-x-auto" data-id="element-2973">
          <button onClick={handleBackToBatches} className={`flex items-center gap-1 hover:text-white transition-colors ${view === 'batches' ? 'text-white font-medium' : 'text-slate-500'}`} data-id="element-2974">
            <HomeIcon size={14} data-id="element-2975" />
            <span data-id="element-2976">All Batches</span>
          </button>

          {selectedBatch && <>
              <ChevronRightIcon size={14} className="text-slate-600" data-id="element-2977" />
              <button onClick={handleBackToInvoices} className={`hover:text-white transition-colors ${view === 'invoices' ? 'text-white font-medium' : 'text-slate-500'}`} data-id="element-2978">
                {selectedBatch.id}
              </button>
            </>}

          {selectedInvoice && <>
              <ChevronRightIcon size={14} className="text-slate-600" data-id="element-2979" />
              <span className="text-white font-medium" data-id="element-2980">
                {selectedInvoice.id}
              </span>
            </>}
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto p-6" data-id="element-2981">
        {view === 'batches' && <div className="animate-in fade-in duration-300" data-id="element-2982">
            <div className="mb-6" data-id="element-2983">
              <h2 className="text-lg font-medium text-white mb-2" data-id="element-2984">
                Recent Batches
              </h2>
              <p className="text-slate-400 text-sm" data-id="element-2985">
                Select a batch to view detailed invoice verification logs.
              </p>
            </div>
            <BatchList batches={MOCK_BATCHES} onSelectBatch={handleBatchSelect} data-id="element-2986" />
          </div>}

        {view === 'invoices' && selectedBatch && <div className="animate-in fade-in slide-in-from-right-4 duration-300" data-id="element-2987">
            <BatchHeader batch={selectedBatch} onBack={handleBackToBatches} data-id="element-2988" />
            <div className="mb-4 flex justify-between items-end" data-id="element-2989">
              <h3 className="text-lg font-medium text-white" data-id="element-2990">
                Invoices ({selectedBatch.invoiceCount})
              </h3>
              <span className="text-xs font-mono text-slate-500" data-id="element-2991">
                CLICK ROW FOR DETAILS
              </span>
            </div>
            <InvoiceList invoices={selectedBatch.invoices} onSelectInvoice={handleInvoiceSelect} data-id="element-2992" />
          </div>}

        {view === 'detail' && selectedInvoice && <div className="animate-in fade-in slide-in-from-right-4 duration-300" data-id="element-2993">
            <button onClick={handleBackToInvoices} className="flex items-center gap-2 text-slate-400 hover:text-white mb-6 text-sm transition-colors" data-id="element-2994">
              <ArrowLeftIcon size={16} data-id="element-2995" />
              <span data-id="element-2996">Back to Batch {selectedBatch?.id}</span>
            </button>
            <VerificationDetail invoice={selectedInvoice} data-id="element-2997" />
          </div>}
      </div>

      {/* Footer */}
      <div className="border-t border-slate-800 mt-12 py-8 text-center" data-id="element-2998">
        <p className="text-xs text-slate-600 font-mono uppercase tracking-wider" data-id="element-2999">
          Confidential | For authorized allocators only | Mint Vault Inc.
        </p>
      </div>
    </div>;
}
function ArrowLeftIcon({
  size,
  className
}: {
  size?: number;
  className?: string;
}) {
  return <svg xmlns="http://www.w3.org/2000/svg" width={size || 24} height={size || 24} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className} data-id="element-3000">
      <path d="m12 19-7-7 7-7" data-id="element-3001" />
      <path d="M19 12H5" data-id="element-3002" />
    </svg>;
}